/**
 * organizationEntitlements — single capability map for Free/Paid plans.
 *
 * Backend `get_organization_entitlements` is authoritative; every helper here
 * only mirrors that response for UX (navigation, queries, screens). Helpers
 * NEVER grant access — RLS/RPC denial still applies. All helpers are sync and
 * safe to call with a null/partial entitlements object.
 *
 * Fail-closed contract: unknown/missing/error state is NOT treated as Free.
 * `isFreeOrg` requires a positively hydrated response (`member === true` and
 * `softwarePlan === 'free'`), so transient failures keep existing behavior
 * (and must never wipe persisted org membership or force logout).
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

// ── Plan / state constants ──────────────────────────────────────────────────
export const SOFTWARE_PLANS = { free: 'free', paid: 'paid' };
export const ACCESS_STATES = {
    free: 'free',
    setupPending: 'setup_pending',
    paid: 'paid',
    billingRestricted: 'billing_restricted',
};
export const PRICING_VERSIONS = { none: 'none', legacyBeta: 'legacy_beta', peakUsersV1: 'peak_users_v1' };

// Mirrors the feature catalogs in public_get_organization_entitlements.sql /
// public_organization_has_feature.sql. Keep in sync with the backend.
export const FREE_MODULES = [
    'people', 'addresses', 'giving', 'notes_admin', 'registrations',
    'settings_basic', 'export_basic',
];
export const PAID_ONLY_MODULES = [
    'tasks', 'events', 'smallgroups', 'training', 'paths', 'boards',
    'calendars', 'conversations', 'files', 'library', 'links', 'songs',
    'forms', 'connect', 'messages', 'music', 'emails', 'locations',
    'analytics', 'attendance', 'assistant', 'sms', 'api_access',
    'custom_fields', 'roles_dynamic', 'quick_filters', 'notifications_module',
];

// Fixed Free action keys returned by the backend in entitlements.actions.
export const FREE_ACTIONS = {
    peopleViewDirectory: 'people.view_directory',
    peopleEditOwnProfile: 'people.edit_own_profile',
    peopleManage: 'people.manage',
    notesView: 'notes.view',
    notesWrite: 'notes.write',
    givingViewOwn: 'giving.view_own',
    givingManage: 'giving.manage',
    usersInvite: 'users.invite',
    usersManageAdmins: 'users.manage_admins',
    dataImportExport: 'data.import_export',
    registrationsManage: 'registrations.manage',
    settingsManageOrg: 'settings.manage_org',
    settingsPersonal: 'settings.personal',
};

// Legacy Paid permission-name → fixed Free action. Any permission name not
// listed resolves to DENY on a Free org (paid-only capability). Used by the
// free can/canAny/canAll adapter attached to the logged-in user profile.
export const LEGACY_PERMISSION_TO_FREE_ACTION = {
    'View People': 'people.view_directory',
    'View Basic Info': 'people.view_directory',
    'View Contact Info': 'people.view_directory',
    'Edit Own Profile': 'people.edit_own_profile',
    'Edit Profile Picture': 'people.edit_own_profile',
    'Create Addresses': 'people.edit_own_profile',
    'Edit Addresses': 'people.edit_own_profile',
    'Create People': 'people.manage',
    'Edit People': 'people.manage',
    'Archive People': 'people.manage',
    'Delete Addresses': 'people.manage',
    'Manage People': 'people.manage',
    'View Notes': 'notes.view',
    'View Contributions': 'giving.manage',
    'Create Contributions': 'giving.manage',
    'Delete Contributions': 'giving.manage',
    'Manage Contributions': 'giving.manage',
    'View Giving': 'giving.manage',
    'View Billing Info': 'settings.manage_org',
    'Invite Users': 'users.invite',
    'Manage Admins': 'users.manage_admins',
    'Export Data': 'data.import_export',
    'Import Data': 'data.import_export',
    'View Registrations': 'registrations.manage',
    'Manage Registrations': 'registrations.manage',
    'Manage Organization Settings': 'settings.manage_org',
    'View Settings': 'settings.personal',
};

// ── Core plan predicates ────────────────────────────────────────────────────
export const isFreeOrg = (ent) => ent?.member === true && ent?.softwarePlan === 'free';
export const isPaidOrg = (ent) => ent?.member === true && ent?.softwarePlan === 'paid';
export const isLegacyPricing = (ent) => ent?.pricingVersion === 'legacy_beta';
export const isNewPeakPricing = (ent) => ent?.pricingVersion === 'peak_users_v1';
export const isSetupPending = (ent) => ent?.accessState === 'setup_pending';
export const isBillingRestricted = (ent) => ent?.accessState === 'billing_restricted';
export const isOrgMember = (ent) => ent?.member === true;

/** Free-plan actor with org management rights (fixed admin/owner rule). */
export const isFreeAdmin = (ent) => isFreeOrg(ent) && (ent?.isAdmin === true || ent?.isAccountOwner === true);

// ── Module / action checks ──────────────────────────────────────────────────
/**
 * Does the plan expose this module? Unknown entitlements → true (preserve
 * existing behavior; backend RLS still enforces). Free → modules[name] must
 * be explicitly true.
 */
export const canViewModule = (ent, name) => {
    if (!isFreeOrg(ent)) return true;
    return ent?.modules?.[name] === true;
};

/**
 * Does the plan allow this fixed Free action? Unknown/non-Free → true (the
 * existing permission layer still applies on Paid).
 */
export const canPerformAction = (ent, action) => {
    if (!isFreeOrg(ent)) return true;
    return ent?.actions?.[action] === true;
};

export const canManagePeople = (ent) => canPerformAction(ent, FREE_ACTIONS.peopleManage);
export const canManageGiving = (ent) => canPerformAction(ent, FREE_ACTIONS.givingManage);
export const canManageOrg = (ent) => canPerformAction(ent, FREE_ACTIONS.settingsManageOrg);
export const canManageRegistrations = (ent) => canPerformAction(ent, FREE_ACTIONS.registrationsManage);
export const canImportExport = (ent) => canPerformAction(ent, FREE_ACTIONS.dataImportExport);
export const canViewNotes = (ent) => canPerformAction(ent, FREE_ACTIONS.notesView);
export const canInviteUsers = (ent) => canPerformAction(ent, FREE_ACTIONS.usersInvite);

// ── can/canAny/canAll adapter (Free) ────────────────────────────────────────
const freePermissionCheck = (actions, permissionName) => {
    const action = LEGACY_PERMISSION_TO_FREE_ACTION[permissionName];
    if (!action) return false; // unknown name → deny on Free
    return actions?.[action] === true;
};

/**
 * Attach plan-aware permission functions to a person object. On Free orgs the
 * functions resolve against the fixed actions map — no role/lock queries. On
 * Paid this returns null and callers keep the existing dynamic behavior.
 */
export const buildFreePermissionFns = (ent) => {
    if (!isFreeOrg(ent)) return null;
    const actions = ent?.actions || {};
    return {
        can: (permission) => freePermissionCheck(actions, permission),
        canAll: (permissions = []) => permissions.every((p) => freePermissionCheck(actions, p)),
        canAny: (permissions = []) => permissions.some((p) => freePermissionCheck(actions, p)),
    };
};

/** Same resolution as buildFreePermissionFns but against a raw actions map (persisted profiles). */
export const freePermissionFromActions = (actions, permissionName) => freePermissionCheck(actions, permissionName);

// ── Navigation: tabs ────────────────────────────────────────────────────────
// Tab option keys a Free org may place in a bottom-tab slot. Members get only
// people/search/settings; admins also get setup Home/giving/registrations/admin.
export const FREE_TAB_OPTION_KEYS = ['home', 'people', 'search', 'settings', 'contributions', 'registrations', 'adminDashboard'];
export const FREE_ADMIN_TAB_OPTION_KEYS = ['home', 'contributions', 'registrations', 'adminDashboard'];

// Per-slot preferred defaults for Free (first allowed wins; fall through so a
// slot never lands on a denied option).
const FREE_SLOT_PREFERENCE = {
    '(home)': ['home', 'people', 'search', 'settings'],
    '(search)': ['search', 'people', 'settings'],
    '(notifications)': ['adminDashboard', 'registrations', 'contributions', 'people', 'settings'],
    '(settings)': ['settings', 'people', 'search'],
};

export const isTabKeyAllowedForPlan = (key, ent) => {
    if (!key) return false;
    if (!isFreeOrg(ent)) return true;
    if (key.startsWith('smallgroups:')) return false;
    if (!FREE_TAB_OPTION_KEYS.includes(key)) return false;
    if (FREE_ADMIN_TAB_OPTION_KEYS.includes(key) && !isFreeAdmin(ent)) return false;
    return true;
};

/**
 * Resolve the effective per-slot tab keys for display. Free orgs sanitize the
 * stored selection against the plan-allowed set — the stored config itself is
 * left untouched so Paid selections survive a downgrade/re-upgrade cycle.
 */
export const resolveEffectiveTabs = (customTabs, ent, defaults) => {
    if (!isFreeOrg(ent)) return customTabs || defaults;
    const resolved = {};
    Object.keys(defaults || {}).forEach((slot) => {
        const preferred = customTabs?.[slot];
        if (isTabKeyAllowedForPlan(preferred, ent)) {
            resolved[slot] = preferred;
            return;
        }
        const candidates = FREE_SLOT_PREFERENCE[slot] || ['people', 'settings'];
        resolved[slot] = candidates.find((key) => isTabKeyAllowedForPlan(key, ent)) || 'people';
    });
    return resolved;
};

export const getDefaultTabKey = (slot, ent, defaults) => {
    if (!isFreeOrg(ent)) return defaults?.[slot];
    const candidates = FREE_SLOT_PREFERENCE[slot] || ['people'];
    return candidates.find((key) => isTabKeyAllowedForPlan(key, ent)) || 'people';
};

// ── Navigation: shared-group route guard ────────────────────────────────────
// Ordered longest-prefix rules evaluated only when isFreeOrg(ent) is true.
// admin → ent.actions['settings.manage_org'-style admin] (isAdmin || isAccountOwner)
// givingEnabled → organization has online giving enabled.
const FREE_ROUTE_RULES = [
    // Paid-only surfaces — explicit denies so they can't become reachable if a
    // broader prefix rule is added later (unmatched prefixes already deny).
    { prefix: '/customfields', deny: true },
    { prefix: '/roles', deny: true },
    { prefix: '/permissions', deny: true },
    // Paid-only admin tooling inside otherwise-allowed settings.
    { prefix: '/settings/organization/api-keys', deny: true },
    { prefix: '/settings/organization/milestones', deny: true },
    { prefix: '/settings/organization/merge-events', deny: true },
    { prefix: '/settings/organization/merge-smallgroups', deny: true },
    // Org-level settings/admin surfaces — fixed Free admin only.
    { prefix: '/settings/organization', admin: true },
    // Personal settings, delete-account, devices stay open to members.
    { prefix: '/settings', allow: true },
    // Creating a person is people.manage — fixed Free admin/owner only.
    { prefix: '/people/create', admin: true },
    { prefix: '/people', allow: true },
    // Address create/edit modals — 'addresses' is a Free module used by the
    // person profile/family flows.
    { prefix: '/addresses', allow: true },
    { prefix: '/search', allow: true }, // backend intersects types to people-only
    // '/notifications' intentionally absent — notifications_module is Paid-only
    // and not part of the fixed Free nav; unmatched prefixes deny.
    { prefix: '/contributions', admin: true },
    { prefix: '/give', givingEnabled: true },
    { prefix: '/guides', allow: true }, // Help
    { prefix: '/registrations', admin: true },
    { prefix: '/import', admin: true }, // export_basic/import for admins
    // Utility modals needed by allowed flows (hosted billing/help links).
    { prefix: '/webview', allow: true },
    { prefix: '/menu', allow: true },
];

/**
 * Route decision for the shared-group layout.
 * @returns {'allow'|'deny'|'redirect-people'}
 */
export const getFreeRouteDecision = (pathname, ent) => {
    if (!isFreeOrg(ent)) return 'allow';
    const path = (pathname || '/').split('?')[0].replace(/\/+$/, '') || '/';
    // Users share the Roles index; role details/editors remain paid-only.
    if (path === '/roles') return isFreeAdmin(ent) ? 'allow' : 'deny';
    // Free admins get setup only; ordinary members still land in People.
    if (path === '/' || path === '/index') return isFreeAdmin(ent) ? 'allow' : 'redirect-people';

    const rule = [...FREE_ROUTE_RULES]
        .sort((a, b) => b.prefix.length - a.prefix.length)
        .find((r) => path === r.prefix || path.startsWith(r.prefix + '/'));
    if (!rule) return 'deny';
    if (rule.deny) return 'deny';
    if (rule.admin && !isFreeAdmin(ent)) return 'deny';
    if (rule.givingEnabled && ent?.enableGiving !== true) return 'deny';
    return 'allow';
};

// ── Secondary surfaces ──────────────────────────────────────────────────────
export const ALL_SEARCH_TYPES = ['people', 'roles', 'tasks', 'events', 'training', 'groups', 'paths', 'boards', 'posts', 'files'];
export const FREE_SEARCH_TYPES = ['people'];
export const allowedSearchTypes = (ent) => (isFreeOrg(ent) ? FREE_SEARCH_TYPES : ALL_SEARCH_TYPES);

// Profile section allowlist consumed by the Free profile split (Phase F2).
export const allowedProfileSections = (ent) => {
    if (!isFreeOrg(ent)) return null; // paid: everything (existing behavior)
    const base = ['details', 'family', 'giving'];
    return isFreeAdmin(ent) ? [...base, 'notes', 'invite', 'export'] : base;
};

// Basic People filter fields available to Free (Phase F2/F3 consumption).
export const FREE_FILTER_FIELDS = [
    'firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'gender',
    'maritalStatus', 'familyRole', 'isArchived', 'city', 'state', 'postalCode',
];
export const allowedFilterFields = (ent) => (isFreeOrg(ent) ? FREE_FILTER_FIELDS : null);

// Advanced-filter fieldIds allowed on Free — mirrors the allowlist in
// public.filter_people ('id','notid','age','gender','createdAt','updatedAt',
// 'invitedBy','hasAccount'). Anything else (customFieldId, roles,
// smallGroupTypes, attendance fields) is rejected server-side with 42501.
export const FREE_PEOPLE_FILTER_FIELD_IDS = ['id', 'notid', 'age', 'gender', 'createdAt', 'updatedAt', 'invitedBy', 'hasAccount'];

// Sort keys the backend rejects on Free (custom field sorts + attendance
// sorts). Everything else in the directory sort menu is Free-safe.
export const FREE_SORT_DENIED_KEYS = ['timesAttended', 'mostRecentVisit', 'firstVisit'];

/**
 * Strip conditions/keys a Free org can't evaluate from a serialized people
 * filter object (persisted filters can outlive a downgrade). Unknown/paid
 * plans return the filters untouched — backend enforcement still applies.
 */
export const sanitizeFiltersForPlan = (filters, ent) => {
    if (!filters || !isFreeOrg(ent)) return filters;
    if (Array.isArray(filters.conditions)) {
        return {
            ...filters,
            conditions: filters.conditions.filter(c => !c?.customFieldId && FREE_PEOPLE_FILTER_FIELD_IDS.includes(c?.fieldId)),
        };
    }
    // Legacy keyed filters (pre-advanced-filter format)
    const PAID_LEGACY_KEYS = ['Roles', 'Small Group Types', 'Times Attended', 'Last Visit', 'First Visit', 'customFieldValues'];
    const cleaned = { ...filters };
    PAID_LEGACY_KEYS.forEach(k => { delete cleaned[k]; });
    return cleaned;
};

/** Filter persisted recently-visited items for display under the current plan. */
export const filterRecentsForPlan = (items, ent) => {
    if (!Array.isArray(items) || !isFreeOrg(ent)) return items;
    return items.filter((item) => {
        const url = item?.url?.split('?')[0];
        return url ? getFreeRouteDecision(url, ent) === 'allow' : false;
    });
};

// ── Latest hydrated snapshot (sync consumers outside React) ─────────────────
let _latestEntitlements = null;
export const setLatestEntitlements = (ent) => { _latestEntitlements = ent || null; };
export const getLatestEntitlements = () => _latestEntitlements;

// ── Plan-transition cache hygiene ───────────────────────────────────────────
const PLAN_MARKER_KEY = '@reachPlanState:v1';

// React-Query root keys (key[0]) allowed to survive a downgrade to Free. The
// purge removes EVERYTHING else — an allowlist (not a paid-root denylist) so
// unknown/added paid roots can't leak stale data under a Free plan.
const FREE_SAFE_QUERY_ROOTS = [
    'loggedInUser', 'person', 'addresses',
    'organizations', 'organizationEntitlements', 'organizationHistory',
    'registrations', 'peopleNotes',
    'contributions', 'batches', 'transactions', 'funds', 'methods', 'merchant',
    'giving', 'paymentMethods', 'recurringGifts', 'orgRecurringGifts',
    'topGivers', 'reportingStats', 'byFund', 'byMethod', 'byMonth', 'bySubject', 'byActor',
    'importTemplates', 'importAssociations',
    'quickPersonFields', 'globalSearch', 'invites', 'registeredUsers',
    'devices', 'guides', 'settings', 'notificationTypes', 'emulatingUser', 'organizationSetup', 'dashboardSections',
];

// Free-allowed roots that are still actor-sensitive: additionally purged when
// the actor lacks the matching fixed action (e.g. member can't read
// notes/registrations or manage giving).
const CONDITIONAL_QUERY_ROOTS = [
    { roots: ['organizationSetup'], action: FREE_ACTIONS.usersManageAdmins },
    { roots: ['contributions', 'batches', 'transactions', 'funds', 'methods', 'merchant', 'giving', 'topGivers', 'reportingStats', 'byFund', 'byMethod', 'byMonth', 'bySubject', 'byActor'], action: FREE_ACTIONS.givingManage },
    { roots: ['peopleNotes'], action: FREE_ACTIONS.notesView },
    { roots: ['registrations'], action: FREE_ACTIONS.registrationsManage },
    { roots: ['importTemplates', 'importAssociations'], action: FREE_ACTIONS.dataImportExport },
    { roots: ['invites'], action: FREE_ACTIONS.usersInvite },
    { roots: ['registeredUsers'], action: FREE_ACTIONS.usersManageAdmins },
];

export const readPlanMarker = async () => {
    try {
        const raw = await AsyncStorage.getItem(PLAN_MARKER_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const writePlanMarker = async (marker) => {
    try {
        await AsyncStorage.setItem(PLAN_MARKER_KEY, JSON.stringify(marker));
    } catch {
        // best-effort marker only
    }
};

/**
 * Compare the freshly hydrated entitlements against the last persisted marker.
 * On org or planRevision change: cancel in-flight requests, drop paid-module
 * caches for Free, invalidate the logged-in user + people variants (the fixed
 * can/canAny/canAll model must rebuild), and persist the new marker.
 * Never throws.
 */
export const applyPlanTransition = async (queryClient, ent) => {
    if (!ent?.member || !queryClient) return;
    const marker = {
        organizationID: ent.organizationID,
        planRevision: ent.planRevision ?? 0,
        softwarePlan: ent.softwarePlan,
    };
    const previous = await readPlanMarker();
    const orgChanged = previous?.organizationID !== marker.organizationID;
    const revisionChanged = previous?.planRevision !== marker.planRevision;
    if (!orgChanged && !revisionChanged) return;

    try {
        // cancelQueries can surface CancelledError from in-flight fetches —
        // swallow it: cancellation is best-effort and must never escape.
        await queryClient.cancelQueries().catch(() => {});

        if (isFreeOrg(ent)) {
            // Allowlist purge: drop every cached query outside the Free-safe
            // root set (stale paid data must never render under a Free plan).
            const safeRoots = new Set(FREE_SAFE_QUERY_ROOTS);
            CONDITIONAL_QUERY_ROOTS.forEach(({ roots: conditional, action }) => {
                if (!canPerformAction(ent, action)) conditional.forEach((root) => safeRoots.delete(root));
            });
            queryClient.removeQueries({ predicate: (query) => !safeRoots.has(query.queryKey?.[0]) });
        } else {
            // Paid revision bump / org change on a paid plan: refresh only the
            // actor-sensitive roots (a downgrade is the destructive case).
            CONDITIONAL_QUERY_ROOTS.forEach(({ roots: conditional }) => {
                if (orgChanged) conditional.forEach((root) => queryClient.removeQueries({ queryKey: [root], exact: false }));
            });
        }

        // Identity/permission state must be rebuilt for the new org/revision —
        // persisted profile functions don't serialize, so force a refetch.
        queryClient.invalidateQueries({ queryKey: ['loggedInUser'] });
        if (orgChanged) {
            queryClient.removeQueries({ queryKey: ['people'], exact: false });
            queryClient.removeQueries({ queryKey: ['organizations'], exact: false });
        }
    } finally {
        await writePlanMarker(marker);
    }
};
